import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ProjectCard, ProjectCardSkeleton } from '../components/ProjectCard';
import { UpdateCard } from '../components/UpdateCard';
import { Button } from '../components/ui/button';
import { Rss, Rocket, FileText, Users } from 'lucide-react';
import { feedAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';

export function FollowingFeedPage() {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');

  useEffect(() => {
    const fetchFeed = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }
      try {
        const res = await feedAPI.getFollowingFeed({ limit: 30 });
        setProjects(res.data.projects || []);
        setUpdates(res.data.updates || []);
      } catch (err) {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    fetchFeed();
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Rss className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">{t('followingFeed.yourFeed')}</h2>
          <p className="text-slate-600 mb-6">{t('followingFeed.loginPrompt')}</p>
          <Link to="/login">
            <Button className="btn-boost">{t('auth.logIn')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  const isEmpty = projects.length === 0 && updates.length === 0;

  const allItems = [
    ...projects.map(p => ({ ...p, _type: 'project', _date: p.created_at })),
    ...updates.map(u => ({ ...u, _type: 'update', _date: u.created_at })),
  ].sort((a, b) => new Date(b._date) - new Date(a._date));

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-1">{t('followingFeed.title')}</h1>
            <p className="text-slate-600">{t('followingFeed.subtitle')}</p>
          </div>
          <Link to="/discover">
            <Button variant="outline" size="sm">
              <Users className="w-4 h-4 mr-1" />
              {t('nav.discover')}
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 gap-6">
            {[...Array(4)].map((_, i) => <ProjectCardSkeleton key={i} />)}
          </div>
        ) : isEmpty ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
            <Users className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">
              {t('followingFeed.empty')}
            </h3>
            <p className="text-slate-500 mb-6">{t('followingFeed.emptyDesc')}</p>
            <Link to="/discover">
              <Button className="btn-boost">{t('followingFeed.discoverProjects')}</Button>
            </Link>
          </div>
        ) : (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="all">
                <Rss className="w-4 h-4 mr-1" /> {t('followingFeed.all')}
              </TabsTrigger>
              <TabsTrigger value="projects">
                <Rocket className="w-4 h-4 mr-1" /> {t('followingFeed.projects')} ({projects.length})
              </TabsTrigger>
              <TabsTrigger value="updates">
                <FileText className="w-4 h-4 mr-1" /> {t('followingFeed.updates')} ({updates.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="all">
              <div className="space-y-6">
                {allItems.map((item) =>
                  item._type === 'project' ? (
                    <ProjectCard key={item.project_id} project={item} />
                  ) : (
                    <UpdateCard key={item.update_id} update={item} />
                  )
                )}
              </div>
            </TabsContent>

            <TabsContent value="projects">
              <div className="grid sm:grid-cols-2 gap-6">
                {projects.map((project) => (
                  <ProjectCard key={project.project_id} project={project} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="updates">
              <div className="space-y-4">
                {updates.map((update) => (
                  <UpdateCard key={update.update_id} update={update} />
                ))}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
